/**
 * A small confirm dialog built on the UI-kit Modal — used before every irreversible/governed action
 * (emission freezes a version; channel delivery is consent-bearing). Focus-trapped + Esc-dismissable
 * by Modal; the confirm button carries the busy state so the action can't be double-fired.
 */
import { Button, Modal } from "@/ui";
import type { ButtonVariant } from "@/ui";
import { useT } from "@/app";

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  confirmVariant?: ButtonVariant;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  confirmVariant = "primary",
  busy = false,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  const { t } = useT();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      dismissable={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t("results.action.cancel")}
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} loading={busy} autoFocus>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, color: "var(--nv-graphite)", lineHeight: 1.5 }}>{body}</p>
    </Modal>
  );
}
